import {
  useCallback,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { NPSDContactInfo } from '@/components/npsd/NPSDContactInfo';

interface NPSDBusinessDetailsProps {
  business: any;
  isEditMode?: boolean;
  phoneOptionTypes?: any[];
  emailOptionTypes?: any[];
  locationExists?: boolean;
  onChange?: (business: any) => void;
  onViewMap?: () => void;
  onLocationBlur?: () => void;
}

export function NPSDBusinessDetails({
  business,
  isEditMode = false,
  phoneOptionTypes = [],
  emailOptionTypes = [],
  locationExists = false,
  onChange,
  onViewMap,
  onLocationBlur,
}: NPSDBusinessDetailsProps) {
  const { theme } = useTheme();

  // Field update helper
  const updateField = useCallback((field: string, value: any) => {
    const updated = { ...business, [field]: value };
    onChange?.(updated);
  }, [business, onChange]);

  // Phone/Email update handlers for shared contact component
  const onPhonesUpdate = useCallback((phones: any[]) => {
    const updated = { ...business, phoneNumbers: phones };
    onChange?.(updated);
  }, [business, onChange]);

  const onEmailsUpdate = useCallback((emails: any[]) => {
    const updated = { ...business, emails: emails };
    onChange?.(updated);
  }, [business, onChange]);

  // Location blur handler
  const handleLocationBlur = useCallback(() => {
    onLocationBlur?.();
  }, [onLocationBlur]);

  return (
    <div className={`npsd-business-details npsd-entity-details${theme ? ' ' + theme : ''}`}>
      <div className="details-layout">
        {/* Main Column (left) */}
        <div className="details-column-main">
          {/* Business Information Panel */}
          <div className="panel business-panel">
            <div className="panel-header">BUSINESS</div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label required">Name</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.businessName || ''}
                    onChange={(e) => updateField('businessName', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Business name"
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Branch Name</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.branchName || ''}
                    onChange={(e) => updateField('branchName', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Branch name"
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Website</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.website || ''}
                    onChange={(e) => updateField('website', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label required">Type</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.businessType || ''}
                    onChange={(e) => updateField('businessType', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Business type"
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Notes</label>
                  <textarea
                    className="kstc-input"
                    rows={3}
                    value={business.notes || ''}
                    onChange={(e) => updateField('notes', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Location Panel */}
          <div className="panel location-panel">
            <div className="panel-header">
              LOCATION
              {locationExists && (
                <button type="button" className="kstc-btn kstc-btn--link" onClick={onViewMap}>
                  View Map
                </button>
              )}
            </div>
            <div className="panel-body">
              <div className="field-row">
                <div className="field-group field-full">
                  <label className="field-label">Address</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.locFormattedAddress || ''}
                    onChange={(e) => updateField('locFormattedAddress', e.target.value)}
                    onBlur={handleLocationBlur}
                    disabled={!isEditMode}
                    placeholder="Street address"
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group field-half">
                  <label className="field-label">Suite/Unit</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.locAddress2 || ''}
                    onChange={(e) => updateField('locAddress2', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Suite, Unit, Apt, etc."
                  />
                </div>
                <div className="field-group field-half">
                  <label className="field-label">Building Name</label>
                  <input
                    type="text"
                    className="kstc-input"
                    value={business.locName || ''}
                    onChange={(e) => updateField('locName', e.target.value)}
                    disabled={!isEditMode}
                    placeholder="Building or location name"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side Column (right) */}
        <div className="details-column-side">
          {/* CONTACT Section - Using shared component */}
          <NPSDContactInfo
            phones={business.phoneNumbers}
            emails={business.emails}
            isReadOnly={!isEditMode}
            phoneTypes={phoneOptionTypes}
            emailTypes={emailOptionTypes}
            title="CONTACT"
            onPhonesChange={onPhonesUpdate}
            onEmailsChange={onEmailsUpdate}
          />
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDBusinessDetailsReact = NPSDBusinessDetails;
